<template>
  <component
    :is="config.componentType"
    v-bind="config.componentProps"
    class="tm-badge"
    :class="{
      [`tm-badge--${color}`]: color,
      [`tm-badge--${appearance}`]: appearance,
      [`tm-badge--${variant}`]: variant,
      'tm-badge--closable': closable,
      'tm-badge--for-button': forButton,
      'tm-badge--disable-hover': isFocusedCloseButton,
    }"
    :aria-frozen="frozen || undefined"
    :aria-disabled="disabled || undefined"
    @click="onClick"
  >
    <slot name="before" />
    <slot>
      <tm-badge-content
        :label="label"
        :icon="icon"
        :additional-text="additionalText"
        :additional-text-visible="additionalTextVisible"
        :text-transform="textTransform"
        :search-query="searchQuery"
        :shrink="shrink"
      />
    </slot>
    <slot name="after" />
    <span
      v-if="closable"
      class="tm-badge__close-button-wrapper"
      @mouseenter="isFocusedCloseButton = true"
      @mouseleave="isFocusedCloseButton = false"
    >
      <tm-button
        class="tm-badge__close-button"
        size="xxSmall"
        flat
        icon-only
        no-border
        rounded
        :disable="disabled"
        @click.prevent.stop="onClickClose"
      >
        <tm-icon
          name="close"
          size="small"
        />
      </tm-button>
    </span>
  </component>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from '@/composition/vue/compositionApi'
import TmBadgeContent from '@/components/shared/badge/TmBadgeContent.vue'
import { TmBadgeContentProps, TmBadgeProps } from '@/components/shared/badge/types'
import TmIcon from '@/components/shared/TmIcon.vue'
import TmButton from '@/components/shared/TmButton.vue'

export default defineComponent({
  components: { TmButton, TmIcon, TmBadgeContent },
  props: {
    ...TmBadgeProps,
    ...TmBadgeContentProps,
  },
  emits: {
    'click-close': (_val: Event) => true,
    click: (_val: Event) => true,
  },
  setup(props, context) {
    const isFocusedCloseButton = ref(false)

    const config = computed(() => {
      if (!props.disabled && !props.frozen) {
        if ((props.variant === 'link' && props.to) || props.to) {
          return {
            componentType: 'router-link',
            componentProps: {
              ariaHidden: props.disabled,
              tabindex: props.disabled ? '-1' : '0',
              to: props.to,
              target: props.isOpenInNewTab ? '_blank' : undefined,
              rel: props.isOpenInNewTab ? 'noopener noreferrer' : undefined,
            },
          }
        }
        if (props.variant === 'button') {
          return {
            componentType: 'button',
            componentProps: {
              ariaHidden: props.disabled,
              tabindex: props.disabled ? '-1' : '0',
              type: 'button',
            },
          }
        }
      }

      return {
        componentType: 'span',
        componentProps: {},
      }
    })

    const onClick = (e: Event) => {
      if (props.disabled || props.frozen) {
        e.preventDefault()
      }
      context.emit('click', e)
    }

    const onClickClose = (e: Event) => {
      if (props.disabled || props.frozen) {
        e.preventDefault()
      }
      context.emit('click-close', e)
    }

    return {
      isFocusedCloseButton,
      config,
      onClick,
      onClickClose,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/mixins';
.tm-badge {
  @include tm-badge;
}

@each $name, $props in $badge-styles {
  .tm-badge--#{$name} {
    --hover-close-button: #{map-get($props, hover-background)};
    color: var(--tm-badge-color, #{map-get($props, color)});
    background-color: var(--tm-badge-background-color, #{map-get($props, background)});
    &.tm-badge--intense {
      color: map-get($props, intense-color);
      background-color: map-get($props, intense-background);
    }
    &.tm-badge--button,
    &.tm-badge--link {
      &:not(.tm-badge--disable-hover):not([aria-disabled='true']):not([aria-frozen='true']):hover,
      &:not(.tm-badge--disable-hover):not([aria-disabled='true']):not([aria-frozen='true']):active {
        background-color: map-get($props, hover-background);
      }
    }
  }
}
</style>
